import { trpc, trpcApi } from '@bitbot/api';
import { createGlobalStore } from 'hox';
import { useWallets } from './list';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  formatValue,
  shortenAddress,
} from '@bitbot/shared/utils/quote-value';
import { Notify } from '@bitbot/ui-design';
import { debounce, keyBy } from 'lodash';
import { useWalletChangeParams } from './common';
import { useGlobalValueStore } from '../trade/common';
import { useToken } from '../user/token';
import { useLogout } from '../user';
import { useAuthentication } from '../trade/common';

export const TOTAL_WALLET_KEY = 'TOTAL_WALLET';

export const [useCurrentWallet] = createGlobalStore(() => {
  const { hasTradeLogin } = useToken();
  const { allParams, showParams } = useWalletChangeParams();
  const {
    data: walletBalance,
    refetch: refetchWalletBalance,
    isLoading: gettingWalletBalance,
    isFetching,
  } = trpc.wallet.getWalletBalance.useQuery(showParams!, {
    enabled:
      !!showParams?.address &&
      hasTradeLogin &&
      showParams?.tokenAddresses.length > 0,
    retry: 5,
  });

  const { data: allAssetBalance } = trpc.wallet.getWalletBalance.useQuery(
    allParams!,
    {
      enabled:
        hasTradeLogin &&
        !!allParams?.address &&
        allParams.tokenAddresses.length > 0,
      retry: 5,
    },
  );

  // const [reciversWhitelist, setReceiversWhitelist] =
  //   useState<Record<string, boolean>>();
  // const checkWhitelistReceiversMutation =
  //   trpc.whitelist.checkWhitelistReceivers.useMutation();
  // useEffect(() => {
  //   if (showParams?.address && showParams.tokenAddresses.length > 0) {
  //     checkWhitelistReceiversMutation
  //       .mutateAsync({
  //         chain: 'base',
  //         ksAddress: showParams?.address,
  //         addresses: showParams.tokenAddresses,
  //       })
  //       .then(data => {
  //         setReceiversWhitelist(data);
  //       });
  //   }
  // }, [showParams]);

  return {
    walletBalance, //base链用户添加的所有 isShoww为true的asset的余额， 包含native和total信息
    walletTokens: walletBalance?.tokens ?? [], //base链用户添加的所有 isShoww为true的asset的余额
    walletTokensObj:
      keyBy(walletBalance?.tokens ?? [], obj => obj.symbol) ?? {},
    allAssetBalance, //base链用户添加的所有asset的余额， 包含native和total信息
    allTokens: allAssetBalance?.tokens ?? [], //base链用户添加的所有asset的余额
    verifiedTotal: walletBalance?.verifiedTotal,
    unverifiedTotal: walletBalance?.unverifiedTotal,
    refetchWalletBalance,
    gettingWalletBalance,
    // reciversWhitelist,
  };
});

export type Token = NonNullable<
  ReturnType<typeof useCurrentWallet>['walletBalance']
>['tokens'][0];

export type WalletHistroyType = Awaited<
  ReturnType<typeof trpcApi.wallet.getWalletHistory.query>
>['history'];

export const useWalletHistory: () => any = () => {
  const { currentWallet } = useWallets();
  const [cursor, setCursor] = useState<string | null>('');
  const [isLoading, setIsLoading] = useState(false);
  const [historyList, setHistoryList] = useState<WalletHistroyType>([]);
  const [noMore, setNoMore] = useState(false);
  const { handleInvalidToken } = useLogout();
  const getWalletHistory = () => {
    setIsLoading(true);
    trpcApi.wallet.getWalletHistory
      .query({
        wallet: currentWallet!,
        chain: 'base',
        cursor: cursor !== '' && cursor !== null ? cursor : undefined,
        limit: 20,
      })
      .then(res => {
        setHistoryList(historyList?.concat(res.history));
        setCursor(res.cursor);
        if (!res.cursor) {
          setNoMore(true);
        }
      })
      .catch(err => {
        handleInvalidToken(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (currentWallet) {
      getWalletHistory();
    }
  }, [currentWallet]);
  // const {
  //   data: historyList,
  //   isLoading,
  //   isFetching,
  // } = trpc.wallet.getWalletHistory.useQuery(
  //   {
  //     wallet: currentWallet!,
  //   },
  //   {
  //     enabled: !!currentWallet,
  //   },
  // );
  return {
    historyList,
    isLoading,
    getWalletHistory,
    history,
    noMore,
    cursor,
  };
};

export const [useSendAsset] = createGlobalStore(() => {
  const router = useRouter();
  const { walletTokens } = useCurrentWallet();
  const { currentWallet, chain } = useWallets();
  const [currentAsset, setCurrentAsset] = useState('');
  const [sendAddress, setSendAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [assetInfo, setAssetInfo] = useState<Token>();
  const [gas, setGas] = useState<any>();
  const [estimatingGas, setEstimatingGas] = useState(false);
  const { setRecentWallets, recentWallets } = useGlobalValueStore();
  const { handleInvalidToken } = useLogout();
  const {
    authCode,
    setAuthCode,
    active,
    setActive,
    otpCodeError,
    setOtpCodeError,
  } = useAuthentication();
  const { data: tokenPriceUsd } = trpc.wallet.getTokenPriceUsd.useQuery(
    { tokenAddress: assetInfo?.tokenAddress!, chain: chain.name },
    {
      enabled: !!assetInfo?.tokenAddress,
    },
  );

  const { data: balance, isLoading: isGettingBalance } =
    trpc.wallet.getTokenBalance.useQuery(
      {
        wallet: currentWallet!,
        tokenAddress: assetInfo?.tokenAddress!,
        showRealWETHBalance: true,
      },
      {
        enabled: !!currentWallet && !!assetInfo?.tokenAddress,
      },
    );

  const checkWhitelistReceiversMutation =
    trpc.whitelist.checkWhitelistReceivers.useMutation();

  const [receiverAllowed, setReceiverAllowed] = useState(false);
  useEffect(() => {
    setReceiverAllowed(false);
  }, [sendAddress]);
  const checkReceiver = () => {
    checkWhitelistReceiversMutation
      .mutateAsync({
        ksAddress: currentWallet!,
        chain: chain.name,
        addresses: [sendAddress],
      })
      .then(data => {
        setReceiverAllowed(data[sendAddress]);
      })
      .catch(e => {
        handleInvalidToken(e.message as string);
      });
  };
  const addWhitelistMutation = trpc.whitelist.addWhitelist.useMutation();
  const [addingWhitelist, setAddingWhitelist] = useState(false);
  const [whitelistRes, setWhitelistRes] = useState<{
    msg: string;
    isSuccess: boolean;
    gasFee: string;
    gasFeeUsd: number;
    hash?: string;
  }>();
  const addWhitelist = (otp: string) => {
    setAddingWhitelist(true);
    addWhitelistMutation
      .mutateAsync({
        otp,
        ksAddress: currentWallet!,
        type: 'receiver',
        chain: chain.name,
        addresses: [sendAddress],
      })
      .then(data => {
        if (data.isSuccess) {
          setWhitelistRes(data);
          setTimeout(() => {
            checkReceiverAndSend();
          }, 2000);
        } else {
          setAddingWhitelist(false);
          if (data.msg === 'Invalid OTP') {
            setOtpCodeError(true);
          } else {
            Notify.error(data.msg);
          }
        }
      })
      .catch(e => {
        if (e.message === 'Invalid OTP') {
          setOtpCodeError(true);
        } else {
          handleInvalidToken(e.message as string);
        }
        setAddingWhitelist(false);
      });
  };

  const checkReceiverAndSend = () => {
    checkWhitelistReceiversMutation
      .mutateAsync({
        chain: chain.name,
        ksAddress: currentWallet!,
        addresses: [sendAddress],
      })
      .then(data => {
        let values = Object.values(data);
        if (values.includes(false)) {
          setTimeout(() => {
            checkReceiverAndSend();
          }, 2000);
        } else {
          //发起send
          setTimeout(() => {
            sendAsset();
          }, 2000);
        }
      })
      .catch(e => {
        handleInvalidToken(e.message as string);
        setAddingWhitelist(false);
      });
  };

  const estimateGas = () => {
    if (
      !!sendAddress &&
      !!currentWallet &&
      assetInfo?.tokenAddress &&
      !!amount
    ) {
      setEstimatingGas(true);
      trpcApi.transfer.sendAmountEstimateGas
        .query({
          token: assetInfo?.tokenAddress,
          to: sendAddress,
          amount: +amount,
          wallet: currentWallet!,
          chain: 'base',
        })
        .then(data => {
          setGas(data);
        })
        .catch(err => {
          Notify.error(err.message);
        })
        .finally(() => {
          setEstimatingGas(false);
        });
    }
  };

  const [isSending, setIsSending] = useState(false);

  const sendAssetMutation = trpc.transfer.sendAmount.useMutation({});
  const sendAsset = debounce(async () => {
    try {
      setIsSending(true);
      const res = await sendAssetMutation.mutateAsync({
        wallet: currentWallet!,
        to: sendAddress,
        amount: +amount,
        chain: 'base',
        token: assetInfo?.tokenAddress,
        gasLimit: gas.gasUnit,
        gasPrice: gas.gasPrice,
      });
      const recentObj = recentWallets?.find(
        item => item.address === sendAddress,
      );
      if (recentObj) {
        recentObj.time = Date.now();
        //重新排序， 按时间降序
        recentWallets?.sort((a, b) => b.time - a.time);
      } else {
        recentWallets?.unshift({
          address: sendAddress,
          time: Date.now(),
          chain: chain.name,
        });
      }
      setRecentWallets([...recentWallets!]);
      router.push(`/assets/send/send-detail?hash=${res.hash}`);
    } catch (error: any) {
      handleInvalidToken(error.message as string);
    } finally {
      setReceiverAllowed(true);
      setIsSending(false);
      setAddingWhitelist(false);
    }
  }, 300);

  useEffect(() => {
    const links = [
      '/assets/send',
      // '/assets/send/how-to-send',
      // '/assets/send/send-input-address',
    ];
    if (sendAddress !== '' && links.includes(router.pathname)) {
      setSendAddress('');
      setAmount('');
      setReceiverAllowed(false);
    }
  }, [router.pathname]);

  useEffect(() => {
    if (currentAsset !== '' && walletTokens.length > 0) {
      const assetInfo = walletTokens.find(
        item => item.symbol === currentAsset,
      );
      if (!assetInfo) {
        router.replace('/');
      }
      setAssetInfo(assetInfo);
    }
  }, [walletTokens, currentAsset]);

  useEffect(() => {
    const asset = router.query.asset as string;
    if (asset) {
      setCurrentAsset(asset);
    }
  }, [router.query]);

  return {
    currentAsset,
    setCurrentAsset,
    assetInfo,
    sendAddress,
    setSendAddress,
    shortAddress: shortenAddress(sendAddress),
    estimateGas,
    sendAsset,
    amount,
    setAmount,
    gas,
    setGas,
    balance: balance?.balanceFormat
      ? formatValue(+balance.balanceFormat, 6)
      : '',
    isGettingBalance,
    tokenPriceUsd,
    isSending,
    addWhitelist,
    addingWhitelist,
    checkReceiver,
    receiverAllowed,
    setWhitelistRes,
    whitelistRes,
    authCode,
    setAuthCode,
    active,
    setActive,
    otpCodeError,
    setOtpCodeError,
    estimatingGas,
    setAddingWhitelist,
  };
});

export const useAssetDetail = () => {
  const router = useRouter();
  const { walletTokens, refetchWalletBalance, walletBalance } =
    useCurrentWallet();
  const { currentWallet, chain } = useWallets();
  const [assetInfo, setAssetInfo] = useState<Token>();
  const [cursor, setCursor] = useState<string | null>('');
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [noMore, setNoMore] = useState(false);
  const ref = useRef(0);
  const { handleInvalidToken } = useLogout();
  const { data: standardToken, isLoading: gettingPairs } =
    trpc.market.getPairFromStandardToken.useQuery(
      {
        token: assetInfo?.tokenAddress!,
        chain: 'base',
        sort: 'h24_tx_count_desc',
      },
      {
        enabled:
          !!assetInfo?.tokenAddress &&
          ((chain.name === 'base' && assetInfo.symbol !== 'ETH') ||
            chain.name !== 'base'),
      },
    );
  const getTokenHistory = () => {
    setIsLoading(true);
    trpcApi.wallet.getWalletTokenTransfer
      .query({
        wallet: currentWallet!,
        chain: 'base',
        cursor: cursor !== '' && cursor !== null ? cursor : undefined,
        limit: 10,
        tokens: [assetInfo?.tokenAddress!],
      })
      .then(res => {
        setHistory(history?.concat(res.transfers));
        if (res.cursor) {
          setCursor(res.cursor);
        } else {
          setNoMore(true);
        }
      })
      .catch(err => {
        handleInvalidToken(err.message as string);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (currentWallet && assetInfo?.symbol && ref.current === 0) {
      ref.current++;
      getTokenHistory();
    }
  }, [currentWallet, assetInfo]);

  useEffect(() => {
    const token = router.query.token as string;
    if (walletTokens.length > 0 && token) {
      const assetInfo = walletTokens.find(item => item.symbol === token);
      if (!assetInfo) {
        router.replace('/assets');
      }
      setAssetInfo(assetInfo);
    }
  }, [walletTokens, router.query]);

  useEffect(() => {
    if (walletBalance) {
      refetchWalletBalance();
    }
  }, [walletBalance]);

  return {
    assetInfo,
    getTokenHistory,
    history,
    isLoading,
    noMore,
    standardToken,
    gettingPairs,
  };
};

export const useTransferDetail = (hash: string) => {
  const ref = useRef(0);
  const { data: detail, isLoading } =
    trpc.wallet.getTransactionReceipt.useQuery(
      {
        hash,
      },
      {
        enabled: !!hash,
        refetchInterval: data => {
          if (data?.status === 1 && ref.current > 5) return false;
          ref.current++;
          return 5000;
        },
      },
    );

  return { detail, isLoading };
};
